import * as React from 'react';

import { Row, Form, Switch } from 'antd';
import { Input, InputNumber, Notification, Space, Text } from 'farmhub-ui-core';
import { useHistory } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'app';
import { ConfirmModal, Footer, PageTitleBar } from 'components';
import MainContent from 'components/Layout/MainContent';
import {
  setBreadcrumb,
  updateEntityLarkBase,
  findOneEntityLarkBase,
  initStockLarkSheet,
} from 'slice';
import { theme } from 'styled';
import { validateEmptyStringFieldForm } from 'utils';

import LarkAppSelect from '../../ConnectLarkPage/components/LarkSelect';
import EntityStockReportSkeleton from '../components/EntityStockReportSkeleton';

type UpdateBodyProps = {
  entityLarkAppID?: string;
  name?: string;
  baseID?: number;
  stockTableID?: number;
  entityProductTableID?: number;
  inputTableID?: number;
  outputTableID?: number;
  larkBotURL?: string;
  minStockQuantity?: number;
  isEnabledNotifyOutOfStock?: boolean;
};

export const EntityStockReportUpdatePage = ({ match }): JSX.Element => {
  const [form] = Form.useForm();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isInitializing, setIsInitializing] = React.useState(false);
  const entityID = useAppSelector(state => state.account.item?.id);
  const [selectEntityLarkAppID, setEntityLarkAppID] = React.useState('');
  const [isEnabledNotifyOutOfStock, setIsEnabledNotifyOutOfStock] =
    React.useState(false);
  const [isVisibleModalConfirm, setIsVisibleModalConfirm] =
    React.useState(false);

  const [isLoadingData, setIsLoadingData] = React.useState(false);

  React.useEffect(() => {
    const initData = async () => {
      setIsLoadingData(true);

      try {
        const { data } = await dispatch(
          findOneEntityLarkBase(match.params.id)
        ).unwrap();

        dispatch(setBreadcrumb({ title: data?.name ?? 'Cập nhật' }));

        form.setFieldsValue({
          name: data.name || '',
          entityLarkAppID: data.entityLarkAppID,
          baseID: data.baseID,
          stockTableID: data.additionalInfo.stockTableID,
          entityProductTableID: data.additionalInfo.entityProductTableID,
          inputTableID: data.additionalInfo.inputTableID,
          outputTableID: data.additionalInfo.outputTableID,
          larkBotURL: data.larkBotURL,
          minStockQuantity: data.minStockQuantity,
          isEnabledNotifyOutOfStock: data.isEnabledNotifyOutOfStock,
          // tableID: '',
        });
        setEntityLarkAppID(data.entityLarkAppID);
        setIsEnabledNotifyOutOfStock(data.isEnabledNotifyOutOfStock);
      } catch (error: any) {
        Notification('error', 'Tải dữ liệu thất bại', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    initData();
  }, [dispatch, form, match.params.id]);

  const handleSubmitBodyUpdate = () => {
    if (entityID) {
      form.validateFields().then(async values => {
        setIsSubmitting(true);

        const updateBody: UpdateBodyProps = {
          entityLarkAppID: selectEntityLarkAppID,
          name: values.name,
          baseID: values.baseID,
          stockTableID: values.stockTableID,
          entityProductTableID: values.entityProductTableID,
          inputTableID: values.inputTableID,
          outputTableID: values.outputTableID,
          larkBotURL: values.larkBotURL,
          minStockQuantity: values.minStockQuantity,
          isEnabledNotifyOutOfStock,
        };

        try {
          await dispatch(
            updateEntityLarkBase({ id: match.params.id, body: updateBody })
          ).unwrap();

          history.push(`/settings/stock-report/list`);
        } catch (error: any) {
          Notification('error', 'Cập nhật báo cáo thất bại', error);
        } finally {
          setIsSubmitting(false);
        }
      });
    }
  };

  const handleChangeLarkApp = async (val: any) => {
    form.setFieldsValue({ entityLarkAppID: val });
    setEntityLarkAppID(val);
  };

  const handleOnChangeSwitch = (val: boolean) => {
    setIsEnabledNotifyOutOfStock(val);
  };

  const handleCreateNewLarkApp = () => {
    // create new lark app
    history.push('/settings/stock-report/connect/create');
  };

  const handleOpenModalInitDataConfirm = () => {
    setIsVisibleModalConfirm(true);
  };

  const handleInitDataForSheet = async () => {
    setIsInitializing(true);
    // init data for sheet

    try {
      await dispatch(initStockLarkSheet(match.params.id)).unwrap();

      Notification('success', 'Khởi tạo dữ liệu thành công');
    } catch (error: any) {
      Notification('error', 'Khởi tạo dữ liệu thất bại', error);
    } finally {
      setIsInitializing(false);
      setIsVisibleModalConfirm(false);
    }
  };

  return (
    <Form form={form}>
      <ConfirmModal
        haveLoading
        isLoading={isInitializing}
        isVisible={isVisibleModalConfirm}
        message="Dữ liệu sẽ được khởi tạo và cập nhật lại dữ liệu cũ!"
        okText="Xác nhận"
        setIsVisible={setIsVisibleModalConfirm}
        title="Bạn có chắn chắn muốn khởi tạo dữ liệu cho file báo cáo!"
        onOke={handleInitDataForSheet}
      />
      <MainContent hasFooter>
        <PageTitleBar title="Cập nhật báo cáo tồn kho" />
        <Space height={25} />
        <Text
          color="dark"
          size="xxs"
          textTransform="uppercase"
          weight="extraBold"
        >
          Thông tin cơ bản
        </Text>
        <Space height={15} />
        {isLoadingData ? (
          <EntityStockReportSkeleton />
        ) : (
          <Form
            form={form}
            labelAlign="left"
            labelCol={{ span: 6 }}
            layout="horizontal"
            wrapperCol={{ span: 11 }}
          >
            <Form.Item
              label="Tiều đề"
              name="name"
              // rules={[
              //   {
              //     required: true,
              //     message: 'Bạn chưa nhập tiêu đề',
              //   },
              // ]}
              rules={validateEmptyStringFieldForm('tiêu đề')}
            >
              <Input
                height={theme.input.height.md}
                placeholder="Nhập tiêu đề tại đây"
              />
            </Form.Item>
            <Form.Item
              label="Ứng dụng Lark:"
              name="entityLarkAppID"
              rules={[
                {
                  required: true,
                  message: 'Bạn chưa nhập chọn ứng dụng Lark',
                },
              ]}
            >
              <LarkAppSelect
                entityID={entityID}
                selectedID={selectEntityLarkAppID}
                onAddLarkApp={handleCreateNewLarkApp}
                onSelect={handleChangeLarkApp}
              />
            </Form.Item>

            <Form.Item
              label="ID LarkBase"
              name="baseID"
              rules={[
                {
                  required: true,
                  message: 'Bạn chưa nhập ID LarkBase',
                },
              ]}
            >
              <Input
                height={theme.input.height.md}
                placeholder="Nhâp ID LarkBase tại đây"
              />
            </Form.Item>
            <Form.Item
              initialValue="tableID"
              label="Thông tin ID bảng"
              name="tableID"
              rules={[
                {
                  required: true,
                  message: 'Bạn chưa nhập thông tin các ID bảng!',
                },
              ]}
            >
              <Row
                justify="space-between"
                style={{ width: '100%', gap: 6, flexWrap: 'nowrap' }}
              >
                <Form.Item
                  name="stockTableID"
                  rules={[
                    {
                      required: true,
                      message: 'Bạn chưa nhập thông tin!',
                    },
                  ]}
                  style={{ marginBottom: '0px', width: '25%' }}
                >
                  <Input
                    height={theme.input.height.md}
                    placeholder="ID bảng tồn kho"
                    width="100%"
                  />
                </Form.Item>
                <Form.Item
                  name="entityProductTableID"
                  rules={[
                    {
                      required: true,
                      message: 'Bạn chưa nhập thông tin!',
                    },
                  ]}
                  style={{ marginBottom: '0px', width: '25%' }}
                >
                  <Input
                    height={theme.input.height.md}
                    placeholder="ID bảng sản phẩm"
                    width="100%"
                  />
                </Form.Item>
                <Form.Item
                  name="inputTableID"
                  rules={[
                    {
                      required: true,
                      message: 'Bạn chưa nhập thông tin!',
                    },
                  ]}
                  style={{ marginBottom: '0px', width: '25%' }}
                >
                  <Input
                    height={theme.input.height.md}
                    placeholder="ID bảng nhập kho"
                    width="100%"
                  />
                </Form.Item>
                <Form.Item
                  name="outputTableID"
                  rules={[
                    {
                      required: true,
                      message: 'Bạn chưa nhập thông tin!',
                    },
                  ]}
                  style={{ marginBottom: '0px', width: '25%' }}
                >
                  <Input
                    height={theme.input.height.md}
                    placeholder="ID bảng xuất kho"
                    width="100%"
                  />
                </Form.Item>
              </Row>
            </Form.Item>

            <Form.Item
              label="Thông báo Lark URL"
              name="larkBotURL"
              rules={[
                {
                  message: 'Bạn chưa nhập thông báo Lark URL',
                },
              ]}
            >
              <Input
                height={theme.input.height.md}
                placeholder="Nhâp đường dẫn URL tại đây"
              />
            </Form.Item>
            <Form.Item
              label="Giá trị dưới định mức"
              name="minStockQuantity"
              rules={[
                {
                  type: 'number',
                  message: 'Bạn chưa nhập giá trị dưới định mức',
                },
              ]}
            >
              <InputNumber
                defaultValue={100}
                height={theme.input.height.md}
                min={1}
                placeholder="Nhập giá trị dưới định mức"
                width="100%"
              />
            </Form.Item>
            <Form.Item
              label="Thông báo khi dưới định mức"
              name="isEnabledNotifyOutOfStock"
              style={{ marginBottom: '0px' }}
            >
              <Switch
                checked={isEnabledNotifyOutOfStock}
                onChange={handleOnChangeSwitch}
              />
            </Form.Item>
          </Form>
        )}
      </MainContent>
      <Footer
        buttons={[
          {
            disabled: false,
            loading: isInitializing,
            content: 'Khởi tạo dữ liệu',
            onClick: () => {
              handleOpenModalInitDataConfirm();
            },
            width: '20px',
          },
        ]}
        content={['Hủy thêm mới', 'Hoàn thành']}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmitBodyUpdate}
      />
    </Form>
  );
};
